// A small card that floats over an element while the mouse is on it, or while it (or a control inside it)
// has keyboard focus: a short explanation that a native title would show late, unstyled, and not at all on
// focus. It is portalled to <body> with a fixed position, so a table that scrolls sideways can't clip it;
// it sits above the element (below when there's no room), clear of the cell-history card that opens under
// a cell. A tap on touch raises it too (the browser's compatibility mouse events), and a tap elsewhere or
// a scroll puts it away. Settings › Display › Hover tips turns them off.
import { useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePrefs } from '../settings/SettingsContext.js';

const GAP = 8;

interface Shown { content: ReactNode; rect: DOMRect }

/** what `bind` gives to spread on an element */
export interface TipHandlers {
  onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onBlur?: () => void;
}

/** `bind(content)` gives the handlers to spread on the element (none when there's nothing to say); render
 *  `node` once. */
export function useHoverTip() {
  const { hoverTips } = usePrefs();
  const [shown, setShown] = useState<Shown | null>(null);
  useEffect(() => {
    if (!shown) return;
    const hide = () => setShown(null);
    document.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [shown]);

  const bind = (content: ReactNode): TipHandlers => {
    if (!hoverTips || content == null || content === false || content === '') return {};
    const show = (e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
      // a wrapper with display: contents has no box of its own: measure what it wraps
      const el = e.currentTarget;
      let rect = el.getBoundingClientRect();
      if (!rect.width && !rect.height && el.firstElementChild) rect = el.firstElementChild.getBoundingClientRect();
      setShown({ content, rect });
    };
    return {
      onMouseEnter: show,
      onMouseLeave: () => setShown(null),
      // only a keyboard focus raises it: a click already has the mouse there
      onFocus: (e: FocusEvent<HTMLElement>) => { if ((e.target as HTMLElement).matches?.(':focus-visible')) show(e); },
      onBlur: () => setShown(null),
    };
  };
  const node = shown ? createPortal(<HoverTipCard {...shown} />, document.body) : null;
  return { bind, node };
}

function HoverTipCard({ content, rect }: Shown) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    // the card's own width (as wide as what it says, up to its max-width), so it centres on the element
    const w = Math.min(el.offsetWidth, window.innerWidth - 2 * GAP);
    const above = rect.top - GAP - h;
    const top = above >= GAP ? above : Math.min(rect.bottom + GAP, window.innerHeight - h - GAP);
    const left = Math.max(GAP, Math.min(rect.left + rect.width / 2 - w / 2, window.innerWidth - w - GAP));
    setPos({ top, left });
  }, [rect]);
  return (
    <div
      ref={ref}
      className="hover-tip"
      role="tooltip"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
    >
      {content}
    </div>
  );
}
